<template>
  <div class="bg-white rounded-xl shadow-sm p-6 min-h-[calc(100vh-2rem)]">
    <div class="flex items-center space-x-4 mb-6">
      <h2 class="text-xl font-bold text-gray-800">问诊记录</h2>
      <div class="bg-gray-100 p-1 rounded-lg flex text-sm font-medium">
        <button 
          @click="changeSource('user')"
          class="px-3 py-1.5 rounded-md transition-all"
          :class="sourceType === 'user' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
        >
          机构问诊
        </button>
        <button 
          @click="changeSource('patient')"
          v-if="canViewPatientHistory"
          class="px-3 py-1.5 rounded-md transition-all"
          :class="sourceType === 'patient' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
        >
          用户问诊
        </button>
      </div>
      
      <!-- <div class="flex items-center space-x-2 flex-1">
        <div class="relative">
          <input 
            type="text" 
            placeholder="检索：用户/症状/病症" 
            class="pl-3 pr-10 py-1.5 border border-gray-200 rounded-md text-sm w-64 focus:outline-none focus:border-green-500"
          >
          <span class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
        </div>
      </div> -->
    </div>

    <div class="overflow-x-auto">
      <table class="w-full text-left text-sm text-gray-600">
        <thead class="bg-gray-50 text-gray-500 font-medium">
          <tr>
            <th class="py-3 px-4">问诊ID</th>
            <th class="py-3 px-4">用户</th>
            <th class="py-3 px-4">手机号</th>
            <th class="py-3 px-4">性别</th>
            <th class="py-3 px-4">年龄</th>
            <th class="py-3 px-4">专科</th>
            <th class="py-3 px-4">症型</th>
            <th class="py-3 px-4">更新时间</th>
            <th class="py-3 px-4 text-center">操作</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-if="!loading && records.length === 0">
            <td colspan="9" class="py-8 px-4 text-center text-gray-400 text-sm">
              暂无问诊记录
            </td>
          </tr>
          <tr
            v-for="item in records"
            :key="item.session_id"
            class="hover:bg-gray-50 transition"
          >
            <td class="py-4 px-4">{{ item.session_id }}</td>
            <td class="py-4 px-4">{{ item.patient_name || '未关联用户' }}</td>
            <td class="py-4 px-4">{{ item.patient_mobile || '' }}</td>
            <td class="py-4 px-4">{{ item.patient_gender || '' }}</td>
            <td class="py-4 px-4">{{ item.patient_age || '' }}</td>
            <td class="py-4 px-4">
              {{ item.disease_name || '' }}
            </td>
            <td class="py-4 px-4">
              {{ item.diagnosis_name || '' }}
            </td>
            <td class="py-4 px-4">
              {{ item.completed_at || item.created_at }}
            </td>
            <td class="py-4 px-4 flex justify-center space-x-2">
              <button
                class="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition"
                @click="openDetailModal(item)"
              >
                查看
              </button>
              <button
                class="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600 transition"
                @click="openDeleteConfirm(item)"
              >
                删除
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex justify-between items-center mt-6 text-sm text-gray-500">
      <div class="flex items-center space-x-2">
        <span>第 {{ page }}/{{ totalPages }} 页 共 {{ total }} 条</span>
        <select class="border border-gray-200 rounded px-2 py-1 focus:outline-none" disabled>
          <option>{{ pageSize }}/页</option>
        </select>
      </div>
      <div class="flex space-x-2">
        <button
          class="px-3 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50"
          :disabled="loading || page <= 1"
          @click="goToPrevPage"
        >
          上一页
        </button>
        <button
          class="px-3 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50"
          :disabled="loading || page >= totalPages || !hasMore"
          @click="goToNextPage"
        >
          下一页
        </button>
      </div>
    </div>

    <div
      v-if="showDetailModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
    >
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 max-h-[80vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-gray-800">问诊详情</h3>
          <button
            class="text-gray-400 hover:text-gray-600"
            @click="closeDetailModal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div v-if="detailLoading" class="py-8 text-center text-gray-500 text-sm">
          加载中...
        </div>
        <div v-else-if="detailError" class="py-4 text-red-500 text-sm">
          {{ detailError }}
        </div>
        <div v-else-if="detailData" class="space-y-6">
          <div class="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <span class="text-gray-400">病症：</span>
              <span>{{ detailData.disease && detailData.disease.name }}</span>
            </div>
            <div>
              <span class="text-gray-400">患者姓名：</span>
              <span>{{ detailRecord && detailRecord.patient_name ? detailRecord.patient_name : '' }}</span>
            </div>
            <div>
              <span class="text-gray-400">手机号：</span>
              <span>{{ detailRecord && detailRecord.patient_mobile ? detailRecord.patient_mobile : '' }}</span>
            </div>
            <div>
              <span class="text-gray-400">性别：</span>
              <span>{{ detailRecord && detailRecord.patient_gender ? detailRecord.patient_gender : '' }}</span>
            </div>
            <div>
              <span class="text-gray-400">年龄：</span>
              <span>{{ detailRecord && (detailRecord.patient_age !== undefined && detailRecord.patient_age !== null) ? detailRecord.patient_age : '' }}</span>
            </div>
            <div>
              <span class="text-gray-400">问诊轮数：</span>
              <span>{{ detailData.answers_count }}</span>
            </div>
            <div>
              <span class="text-gray-400">开始时间：</span>
              <span>{{ detailData.created_at }}</span>
            </div>
            <div>
              <span class="text-gray-400">结束时间：</span>
              <span>{{ detailData.completed_at }}</span>
            </div>
          </div>

          <div class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">
              问答记录
            </h4>
            <div v-if="detailData.answers && detailData.answers.length" class="space-y-3 text-sm">
              <div
                v-for="answer in detailData.answers"
                :key="answer.step_number"
                class="p-3 rounded-lg bg-gray-50"
              >
                <div class="text-gray-500 text-xs mb-1">
                  第 {{ answer.step_number }} 题
                </div>
                <div class="text-gray-800 mb-1">
                  {{ answer.question_text }}
                </div>
                <div class="text-gray-600">
                  回复：{{ (answer.selected_option_texts || []).join('、') }}
                </div>
              </div>
            </div>
            <div v-else class="text-gray-400 text-sm">
              暂无问答记录
            </div>
          </div>

          <div v-if="detailData.tongue_image" class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">舌苔图</h4>
            <div class="bg-gray-50 px-3 py-3 rounded">
              <img 
                :src="detailData.tongue_image" 
                alt="舌苔图" 
                class="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
                @click="openImagePreview(detailData.tongue_image)"
              />
            </div>
          </div>

          <div class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">
              诊断结果
            </h4>
            <div v-if="detailData.diagnosis_result" class="space-y-2 text-sm text-gray-700">
              <div>
                <span class="text-gray-400">倾向证型：</span>
                <span>{{ detailData.diagnosis_result.diagnosis_name }}</span>
              </div>
              <div>
                <span class="text-gray-400">症状群：</span>
                <span>{{ (detailData.diagnosis_result.symptom_groups || []).join('、') }}</span>
              </div>
              <!-- <div>
                <span class="text-gray-400">结果置信度：</span>
                <span>{{ detailData.diagnosis_result.confidence_level }}</span>
              </div> -->
              <div>
                <span class="text-gray-400">核心病机：</span>
                <span>{{ detailData.diagnosis_result.core_pathogenesis }}</span>
              </div>
              <div>
                <span class="text-gray-400">鉴别排除：</span>
                <span>{{ (detailData.diagnosis_result.differential_exclusions || []).join('、') }}</span>
              </div>
              <div>
                <span class="text-gray-400">建议方剂：</span>
                <span>{{ detailData.diagnosis_result.recommended_prescription }}</span>
              </div>
              <div
                v-if="
                  detailData &&
                  detailData.status === 'completed' &&
                  detailData.diagnosis_result &&
                  Array.isArray(detailData.diagnosis_result.branch_matchings) &&
                  detailData.diagnosis_result.branch_matchings.length
                "
                class="mt-1"
              >
                <div class="text-gray-400 mb-1">证型匹配：</div>
                <div class="space-y-1">
                  <div
                    v-for="(item, index) in detailData.diagnosis_result.branch_matchings"
                    :key="index"
                    class="flex items-center justify-between bg-gray-50 px-3 py-2 rounded"
                  >
                    <span class="text-gray-700">{{ item.pattern_name }}</span>
                    <span class="text-primary-green text-sm font-medium">
                      {{ formatMatchPercentage(item.match_percentage) }}
                    </span>
                  </div>
                </div>
              </div>
              <div
                v-if="detailData && detailData.diagnosis_result && (shouldShowDetailAiButton || detailAiConsultationLoading || (detailData.diagnosis_result.ai_diagnosis && String(detailData.diagnosis_result.ai_diagnosis).trim()))"
              >
                <div class="flex items-center justify-between mb-1">
                  <div class="text-gray-400">深度辨证分析：</div>
                  <button
                    v-if="shouldShowDetailAiButton"
                    class="px-3 py-1 bg-primary-green text-white rounded text-xs hover:bg-green-600 transition disabled:opacity-50"
                    :disabled="detailAiConsultationLoading || detailAiConsultationCompleted"
                    @click="startDetailAiConsultation"
                  >
                    {{ detailAiConsultationLoading ? '分析中...' : (detailAiConsultationCompleted ? '已完成' : '深度辨证') }}
                  </button>
                </div>
                <div class="bg-gray-50 px-3 py-2 rounded text-gray-700 whitespace-pre-line">
                  <span>{{ detailData.diagnosis_result.ai_diagnosis }}</span>
                  <span
                    v-if="detailAiConsultationLoading"
                    class="inline-block w-1.5 h-4 bg-primary-green animate-pulse ml-1 align-middle"
                  ></span>
                </div>
              </div>
            </div>
            <div v-else class="text-gray-400 text-sm">
              暂无诊断结果
            </div>
          </div>

          <div class="flex justify-end mt-4">
            <button
              class="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition"
              @click="closeDetailModal"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
    >
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 transform transition-all scale-100">
        <h3 class="text-xl font-bold text-gray-800 mb-4">确认删除</h3>
        <p class="text-gray-600 mb-6">
          确定要删除该问诊记录吗？
        </p>
        
        <div class="flex justify-end space-x-3">
          <button
            @click="closeDeleteConfirm"
            class="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition"
            :disabled="deleting"
          >
            取消
          </button>
          <button
            @click="confirmDelete"
            class="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition shadow-sm disabled:opacity-70"
            :disabled="deleting"
          >
            {{ deleting ? '删除中...' : '确认删除' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 图片放大预览 -->
    <div 
      v-if="previewImageUrl" 
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90"
      @click="closeImagePreview"
    >
      <div class="relative max-w-full max-h-full p-4">
        <button 
          class="absolute top-0 right-0 p-2 text-white hover:text-gray-300"
          @click.stop="closeImagePreview"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img :src="previewImageUrl" class="max-w-full max-h-[90vh] object-contain rounded" @click.stop />
      </div>
    </div>

  </div>
</template>

<script>
import { httpRequest, parsePagination } from '@/utils/request'

export default {
  name: 'DiagnosisRecords',
  data() {
    return {
      sourceType: 'user', // 'user' | 'patient'
      canViewPatientHistory: true,
      records: [],
      page: 1,
      totalPages: 1,
      total: 0,
      pageSize: 10,
      hasMore: false,
      loading: false,
      showDetailModal: false,
      detailLoading: false,
      detailError: '',
      detailData: null,
      detailRecord: null,
      showDeleteConfirm: false,
      deleting: false,
      recordToDelete: null,
      patientId: null,
      detailSessionId: null,
      detailAiConsultationLoading: false,
      detailAiConsultationCompleted: false,
      detailAiConsultationAbortController: null,
      detailAiConsultationCancelled: false,
      previewImageUrl: ''
    }
  },
  computed: {
    shouldShowDetailAiButton() {
      if (!this.detailData || !this.detailData.diagnosis_result) return false
      if (this.detailData.status !== 'completed') return false
      const confirmed_value = this.detailData.diagnosis_result.confirmed
      const confirmed_false = confirmed_value === false || confirmed_value === 0 || confirmed_value === '0'
      return confirmed_false
    }
  },
  mounted() {
    const query_patient_id = this.$route && this.$route.query ? this.$route.query.patient_id : null
    this.patientId = query_patient_id || null
    this.loadPermissions()
    this.fetchHistory(1)
  },
  watch: {
    '$route.query.patient_id'(newVal) {
      this.patientId = newVal || null
      this.page = 1
      this.hasMore = false
      this.fetchHistory(1)
    }
  },
  methods: {
    loadPermissions() {
      let cached = null
      try {
        const raw = localStorage.getItem('user_profile')
        if (raw) cached = JSON.parse(raw)
      } catch (e) {
        cached = null
      }
      const can_view = cached && typeof cached.can_view_patient_diagnosis_history === 'boolean'
        ? cached.can_view_patient_diagnosis_history
        : true
      this.canViewPatientHistory = can_view
      if (!can_view && this.sourceType === 'patient') {
        this.sourceType = 'user'
      }
    },
    changeSource(type) {
      if (this.sourceType === type || this.loading) return
      if (type === 'patient' && !this.canViewPatientHistory) {
        alert('暂无权限查看用户问诊记录')
        return
      }
      this.sourceType = type
      this.page = 1
      this.records = []
      this.fetchHistory(1)
    },
    async fetchHistory(targetPage = 1) {
      if (this.loading) return
      this.loading = true
      try {
        const query = {
          page: targetPage,
          role: this.sourceType
        }
        if (this.patientId) {
          query.patient_id = this.patientId
        }
        const response = await httpRequest('get', '/v1/diagnosis/special/history', {
          headers: {
            'Client-Type': '1'
          },
          query
        })
        const pagination = parsePagination(response, targetPage, 'data')
        this.records = pagination.list
        this.page = pagination.page
        this.hasMore = pagination.has_more

        const meta = response && response.data && response.data.meta ? response.data.meta : {}
        const p = meta.pagination || {}
        if (typeof p.total === 'number') {
          this.total = p.total
        } else {
          this.total = this.records.length
        }
        if (typeof p.per_page === 'number') {
          this.pageSize = p.per_page
        }
        if (typeof p.total_pages === 'number') {
          this.totalPages = p.total_pages
        } else {
          this.totalPages = this.hasMore ? this.page + 1 : this.page
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    goToPrevPage() {
      if (this.page <= 1 || this.loading) return
      const target = this.page - 1
      this.fetchHistory(target)
    },
    goToNextPage() {
      if (this.loading || this.page >= this.totalPages || !this.hasMore) return
      const target = this.page + 1
      this.fetchHistory(target)
    },
    openDetailModal(record) {
      if (!record || !record.session_id) return
      this.showDetailModal = true
      this.detailLoading = true
      this.detailError = ''
      this.detailData = null
      this.detailRecord = record
      this.detailSessionId = record.session_id
      this.detailAiConsultationLoading = false
      this.detailAiConsultationCompleted = false
      this.detailAiConsultationCancelled = false
      if (this.detailAiConsultationAbortController) {
        try {
          this.detailAiConsultationAbortController.abort()
        } catch (e) {
          // ignore
        }
      }
      this.detailAiConsultationAbortController = null
      this.fetchDetail(record.session_id)
    },
    closeDetailModal() {
      if (this.detailLoading) return
      this.detailAiConsultationCancelled = true
      if (this.detailAiConsultationAbortController) {
        try {
          this.detailAiConsultationAbortController.abort()
        } catch (e) {
          // ignore
        }
      }
      this.detailAiConsultationAbortController = null
      this.detailAiConsultationCancelled = false
      this.showDetailModal = false
      this.detailRecord = null
    },
    openImagePreview(url) {
      if (!url) return
      this.previewImageUrl = url
    },
    closeImagePreview() {
      this.previewImageUrl = ''
    },
    async fetchDetail(sessionId) {
      try {
        const response = await httpRequest('get', `/v1/diagnosis/special/session/${sessionId}/detail`, {
          headers: {
            'Client-Type': '1'
          }
        })
        this.detailData = response && response.data ? response.data : null
        const ai_text =
          this.detailData &&
          this.detailData.diagnosis_result &&
          this.detailData.diagnosis_result.ai_diagnosis
            ? String(this.detailData.diagnosis_result.ai_diagnosis)
            : ''
        this.detailAiConsultationCompleted = !!ai_text.trim()
      } catch (error) {
        this.detailError = error.message || '加载详情失败'
      } finally {
        this.detailLoading = false
      }
    },
    async startDetailAiConsultation() {
      if (this.detailAiConsultationLoading || this.detailAiConsultationCompleted) return
      if (!this.detailSessionId) return
      if (!this.detailData || !this.detailData.diagnosis_result) return

      this.detailAiConsultationLoading = true
      this.detailAiConsultationCompleted = false
      this.detailAiConsultationCancelled = false
      this.detailData.diagnosis_result.ai_diagnosis = ''

      try {
        const abort_controller = new AbortController()
        this.detailAiConsultationAbortController = abort_controller

        const response = await httpRequest('get', `/v1/diagnosis/special/session/${this.detailSessionId}/ai`, {
          headers: {
            'Client-Type': '1',
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache'
          },
          query: {
            stream: '1'
          },
          stream: true,
          timeout: 0,
          signal: abort_controller.signal
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        if (!response.body) {
          throw new Error('Response body is null')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()

        let ui_buffer = ''
        let last_flush_ms = 0
        const flush_interval_ms = 50
        const flushToUi = async () => {
          if (!ui_buffer) return
          if (this.detailData && this.detailData.diagnosis_result) {
            this.detailData.diagnosis_result.ai_diagnosis += ui_buffer
          }
          ui_buffer = ''
          await new Promise((resolve) => requestAnimationFrame(resolve))
        }

        let pending_text = ''
        let done = false
        while (!done) {
          const { done: streamDone, value } = await reader.read()
          done = streamDone
          if (value) {
            pending_text += decoder.decode(value, { stream: true })
            pending_text = pending_text.replace(/\r\n/g, '\n')

            let event_end_index = pending_text.indexOf('\n\n')
            while (event_end_index !== -1) {
              const raw_event = pending_text.slice(0, event_end_index)
              pending_text = pending_text.slice(event_end_index + 2)

              const lines = raw_event.split('\n')
              const data_lines = lines
                .filter((line) => line.startsWith('data:'))
                .map((line) => line.replace(/^data:\s?/, ''))
              const data_text = data_lines.join('\n').trim()

              if (data_text) {
                if (data_text === '[DONE]') {
                  done = true
                  break
                }

                let appended = false
                let parsed_ok = false
                try {
                  const parsed = JSON.parse(data_text)
                  parsed_ok = true
                  if (parsed && typeof parsed.delta === 'string') {
                    ui_buffer += parsed.delta
                    appended = true
                  }
                } catch (e) {
                  // ignore
                }

                if (!appended && !parsed_ok) {
                  ui_buffer += data_text
                }

                const now_ms = Date.now()
                if (now_ms - last_flush_ms >= flush_interval_ms) {
                  last_flush_ms = now_ms
                  await flushToUi()
                }
              }

              event_end_index = pending_text.indexOf('\n\n')
            }
          }

          if (this.detailAiConsultationCancelled) {
            done = true
          }
        }

        pending_text += decoder.decode()
        await flushToUi()

        if (!this.detailAiConsultationCancelled) {
          this.detailAiConsultationCompleted = true
        }
      } catch (error) {
        console.error(error)
        if (!this.detailAiConsultationCancelled && this.detailData && this.detailData.diagnosis_result) {
          this.detailData.diagnosis_result.ai_diagnosis += `\n\n(获取结果出错: ${error.message || '未知错误'})`
        }
      } finally {
        this.detailAiConsultationLoading = false
        this.detailAiConsultationAbortController = null
      }
    },
    formatStatus(status) {
      if (status === 'in_process') {
        return '未完成'
      }
      if (status === 'completed') {
        return '已完成'
      }
      if (status === 'canceld') {
        return '已取消'
      }
      return status || ''
    },
    formatMatchPercentage(value) {
      if (value === null || value === undefined) {
        return ''
      }
      if (typeof value === 'number') {
        return `${value}%`
      }
      const str = String(value)
      if (str.indexOf('%') !== -1) {
        return str
      }
      return `${str}%`
    },
    openDeleteConfirm(record) {
      if (!record || !record.session_id) return
      this.recordToDelete = record
      this.showDeleteConfirm = true
      this.deleting = false
    },
    closeDeleteConfirm() {
      if (this.deleting) return
      this.showDeleteConfirm = false
      this.recordToDelete = null
    },
    async confirmDelete() {
      if (!this.recordToDelete || !this.recordToDelete.session_id || this.deleting) {
        return
      }
      this.deleting = true
      try {
        await httpRequest('delete', `/v1/diagnosis/special/session/${this.recordToDelete.session_id}`, {
          headers: {
            'Client-Type': '1'
          }
        })
        this.showDeleteConfirm = false
        this.recordToDelete = null
        this.fetchHistory(this.page)
      } catch (error) {
        console.error(error)
        this.deleting = false
      }
    }
  }
}
</script>
